"use client"

import * as React from "react"
import {format, getMonth, getYear, setMonth, setYear, subYears} from "date-fns"
import {Calendar as CalendarIcon} from "lucide-react"

import {cn} from "@/lib/utils"
import {Button} from "@/components/ui/button"
import {Calendar} from "@/components/ui/calendar"
import {Popover, PopoverContent, PopoverTrigger,} from "@/components/ui/popover"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "./select"
import { it } from "date-fns/locale"

interface DatePickerProps {
    startYear?: number;
    endYear?: number;
    className?: string;
    label?: string;
    value?: Date | null;  // Aggiungi un valore controllato
    onDateChange?: (date: Date) => void;  // Aggiungi un callback per restituire la data selezionata
}

export function DatePicker({
                               startYear = getYear(subYears(new Date(), 100)),
                               endYear = getYear(subYears(new Date(), 19)),
                               className,
                               value = null, // Usa il valore controllato come default
                               label = "Select a date",
                               onDateChange,
                           }: DatePickerProps) {

    const [date, setDate] = React.useState<Date | null>(null);

    React.useEffect(() => {
        // Quando `value` cambia, aggiorna lo stato del `DatePicker` per essere sincronizzato
        setDate(value);
    }, [value]);

    const months = [
        'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
    ];
    const years = Array.from(
        { length: endYear - startYear + 1 },
        (_, i) => endYear - i
    );

    const handleMonthChange = (month: string) => {
        const current = date || subYears(new Date(), 19); // Usa una data di default se `date` è null
        const newDate = setMonth(current, months.indexOf(month));
        setDate(newDate);
        onDateChange?.(newDate);  // Richiama il callback se definito
    }

    const handleYearChange = (year: string) => {
        const current = date || subYears(new Date(), 19); // Usa una data di default se `date` è null
        const newDate = setYear(current, parseInt(year));
        setDate(newDate);
        onDateChange?.(newDate);  // Richiama il callback se definito
    }

    const handleSelect = (selectedData: Date | undefined) => {
        if (selectedData) {
            setDate(selectedData);
            onDateChange?.(selectedData);  // Richiama il callback se definito
        }
    }

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant={"outline"}
                    className={cn(
                        "w-full justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4"/>
                    {date ? format(date, "PPP", {locale: it}) : <span>{label}</span>} {/* Usa la localizzazione italiana */}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <div className="flex justify-between p-2">
                    <Select
                        onValueChange={handleMonthChange}
                        value={date ? months[getMonth(date)] : undefined}
                    >
                        <SelectTrigger className="w-[110px]">
                            <SelectValue placeholder="Mese"/>
                        </SelectTrigger>
                        <SelectContent>
                            {months.map(month => (
                                <SelectItem key={month} value={month}>{month}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select
                        onValueChange={handleYearChange}
                        value={date ? getYear(date).toString() : undefined}
                    >
                        <SelectTrigger className="w-[110px]">
                            <SelectValue placeholder="Anno"/>
                        </SelectTrigger>
                        <SelectContent>
                            {years.map(year => (
                                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Calendar
                    mode="single"
                    selected={date || undefined}
                    onSelect={handleSelect}
                    initialFocus
                    month={date || undefined}
                    disabled={(d) =>
                        d > subYears(new Date(), 19) || d < subYears(new Date(), 100)
                    }
                    onMonthChange={setDate}
                />
            </PopoverContent>
        </Popover>
    )
}
